/**
 * usePermission — Centralised permission/role composable.
 *
 * Usage in any component:
 *
 *   import { usePermission } from '@/composables/usePermission'
 *   const { can, canAny, canAll, hasRole, hasAnyRole, isAdmin } = usePermission()
 *
 *   // in template
 *   v-if="can('projects.create')"
 *   v-if="canAny(['projects.approve', 'programs.approve'])"
 *   v-if="hasRole('PMO Manager')"
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { showWarning } from '@/utils/toast.js'

export function usePermission() {
  const authStore = useAuthStore()

  // ── Role helpers ────────────────────────────────────────────────────────

  /** True when the user has ALL listed super-admin/admin roles (full bypass). */
  const isAdmin = computed(() =>
    authStore.hasAnyRole(['Super Admin', 'Admin'])
  )

  /**
   * Check if the user has a specific role.
   * @param {string} role
   */
  const hasRole = (role) => authStore.hasRole(role)

  /**
   * Check if the user has ANY of the given roles.
   * @param {string[]} roles
   */
  const hasAnyRole = (roles) => authStore.hasAnyRole(roles)

  /**
   * Check if the user has ALL of the given roles.
   * @param {string[]} roles
   */
  const hasAllRoles = (roles) => authStore.hasAllRoles(roles)

  // ── Permission helpers ──────────────────────────────────────────────────

  /**
   * Check if the user has a specific permission.
   * Super Admin / Admin always return true.
   *
   * @param {string} permission
   * @returns {boolean}
   */
  const can = (permission) => {
    if (isAdmin.value) return true
    return authStore.hasPermission(permission)
  }

  /**
   * Check if the user has ANY of the given permissions.
   * Super Admin / Admin always return true.
   *
   * @param {string[]} permissions
   * @returns {boolean}
   */
  const canAny = (permissions) => {
    if (isAdmin.value) return true
    return authStore.hasAnyPermission(permissions)
  }

  /**
   * Check if the user has ALL of the given permissions.
   * Super Admin / Admin always return true.
   *
   * @param {string[]} permissions
   * @returns {boolean}
   */
  const canAll = (permissions) => {
    if (isAdmin.value) return true
    return authStore.hasAllPermissions(permissions)
  }

  /**
   * Inverse of can() — true when the user CANNOT do something.
   * @param {string} permission
   */
  const cannot = (permission) => !can(permission)

  /**
   * Return the first truthy value from a map of permission → value pairs.
   * Useful for conditional labels / icon variants.
   *
   * Example:
   *   permSwitch({ 'projects.manage': 'full', 'projects.read': 'read-only' }, 'none')
   *
   * @param {Record<string,*>} map
   * @param {*} fallback
   */
  const permSwitch = (map, fallback = null) => {
    for (const [perm, val] of Object.entries(map)) {
      if (can(perm)) return val
    }
    return fallback
  }

  // ── Computed shortcuts (reactive) ───────────────────────────────────────
  // These are reactive computed refs so they can be used in watchers / templates.

  const userPermissionNames = computed(() =>
    (authStore.userPermissions || []).map((p) =>
      typeof p === 'string' ? p : p?.name
    ).filter(Boolean)
  )

  const userRoleNames = computed(() => authStore.userRoleNames || [])

  // ── Strategic Plan approved-plan restriction helpers ────────────────────

  /**
   * Tabs/sections that Strategy Manager and SMO cannot modify once a plan is approved.
   * Allowed tabs (Risks, Challenges, Corrective Plans, Performance Analytics) are NOT listed here.
   */
  const APPROVED_PLAN_RESTRICTED_TABS = [
    'kpis', 'update_kpis', 'kpi_change_requests',
    'initiatives', 'update_initiatives', 'initiative_change_requests', 'initiative_closure',
  ]

  const APPROVED_PLAN_RESTRICTED_ROLES = ['Strategy Manager', 'SMO']

  /**
   * True when the current user is a Strategy Manager or SMO (but not Admin).
   * These roles cannot write to restricted tabs on approved plans.
   */
  const isApprovedPlanRestrictedRole = computed(
    () => !isAdmin.value && hasAnyRole(APPROVED_PLAN_RESTRICTED_ROLES)
  )

  /**
   * Returns true when the user is allowed to modify the given tab on the given plan.
   *
   * @param {object|null} plan       - Strategic plan object (must have a `status` property)
   * @param {string}      tabName    - Tab identifier, e.g. 'kpis', 'risks', 'initiatives'
   * @returns {boolean}
   */
  const canModifyStrategicPlanTab = (plan, tabName) => {
    if (isAdmin.value) return true
    if (plan?.status !== 'approved') return true        // plan not approved — normal perms apply
    if (!isApprovedPlanRestrictedRole.value) return true // role not restricted
    return !APPROVED_PLAN_RESTRICTED_TABS.includes(tabName?.toLowerCase())
  }

  return {
    // Role
    isAdmin,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    userRoleNames,

    // Permissions
    can,
    canAny,
    canAll,
    cannot,
    permSwitch,
    userPermissionNames,

    // Permission guard helpers
    /**
     * Show a warning toast when the user lacks permission.
     * @param {string} [message] - Custom message (defaults to generic one)
     */
    warnNoPermission(message = 'You do not have permission to perform this action.') {
      showWarning(message, 'Access Denied')
    },

    /**
     * Wrap an action function with a permission check.
     * If the user lacks the permission, shows a warning and does nothing.
     *
     * @param {string|string[]} permission  - Permission name or array (uses canAny)
     * @param {Function}        action      - The action to run if allowed
     * @param {string}          [message]   - Optional custom warning message
     *
     * Usage:
     *   withPermission('roles.manage', () => savePermissions())
     *   withPermission(['projects.create', 'projects.manage'], () => doSomething(), 'Custom msg')
     */
    withPermission(permission, action, message) {
      const allowed = Array.isArray(permission)
        ? (isAdmin.value || authStore.hasAnyPermission(permission))
        : can(permission)
      if (!allowed) {
        const msg = message || 'You do not have permission to perform this action.'
        showWarning(msg, 'Access Denied')
        return false
      }
      return action()
    },

    // Strategic plan tab restriction
    isApprovedPlanRestrictedRole,
    canModifyStrategicPlanTab,
  }
}
